import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP } from 'components/financepro';
import { escHtml } from 'facturapos/documentoEmpresarial';
import { cardShellSx, cellHeadSx, formatearFecha } from './entradaDiarioShared';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const moneyHtml = (n) => `$${fmtMoney(n)}`;

const etiquetaStatus = (status, tipo) => {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'C') return 'Pagada';
  const t = String(tipo || '').trim().toLowerCase();
  if (t === 'contado') return 'Contado';
  return 'Pendiente';
};

const Dato = ({ label, value, strong = false }) => (
  <Box>
    <Typography
      sx={{
        fontSize: '0.7rem',
        fontWeight: 700,
        color: FP.color.textMuted,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: strong ? 800 : 700,
        fontSize: strong ? '1.02rem' : '0.92rem',
        color: FP.color.textPrimary,
        wordBreak: 'break-word',
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const SeccionTabla = ({ titulo, columnas, filas, empty, footer }) => (
  <Paper elevation={0} sx={{ ...cardShellSx, mb: 2.5 }}>
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        py: 1.6,
        borderBottom: `1px solid ${FP.color.border}`,
        bgcolor: '#FAFBFE',
      }}
    >
      <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
        {titulo}
      </Typography>
    </Box>
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
      >
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableBody>
            <TableRow
              sx={{
                '& td': {
                  border: 'none',
                  borderBottom: `2px solid ${FP.color.border}`,
                  bgcolor: FP.color.surfaceAlt,
                },
              }}
            >
              {columnas.map((c) => (
                <TableCell key={c.id} align={c.align || 'left'}>
                  <Typography
                    component="span"
                    sx={{
                      ...cellHeadSx,
                      textAlign: c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : 'left',
                      width: '100%',
                    }}
                  >
                    {c.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            {!filas || filas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnas.length} align="center" sx={{ py: 3, color: FP.color.textMuted }}>
                  {empty || 'Sin registros'}
                </TableCell>
              </TableRow>
            ) : (
              filas.map((row, i) => (
                <TableRow key={row.id != null ? `${titulo}-${row.id}` : `${titulo}-${i}`} hover>
                  {columnas.map((c) => (
                    <TableCell
                      key={c.id}
                      align={c.align || 'left'}
                      sx={{
                        fontWeight: c.strong ? 700 : 400,
                        whiteSpace: c.nowrap === false ? 'normal' : 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={c.title ? c.title(row) : undefined}
                    >
                      {c.render ? c.render(row) : row[c.id] || '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {footer}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </Paper>
);

const generarHtmlFactura = ({
  nombreEmpresa,
  direccionEmpresa,
  rncEmpresa,
  factura,
}) => {
  const filasLineas =
    !(factura.lineas || []).length
      ? `<tr><td colspan="4" style="text-align:center;padding:18px;color:#888;">Sin líneas de distribución</td></tr>`
      : factura.lineas
          .map(
            (ln, idx) => `
        <tr style="background:${idx % 2 === 0 ? '#ffffff' : '#f7f8fa'};">
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;font-size:12px;">${escHtml(ln.cuenta || '—')}</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;font-size:12px;">${escHtml(ln.nombre || '—')}</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;font-size:12px;">${escHtml(ln.localidad || '—')}</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;">${moneyHtml(ln.valor)}</td>
        </tr>`
          )
          .join('');

  const ncf = factura.ncf && factura.ncf.ncf ? factura.ncf : null;
  const ncfHtml = ncf
    ? `<div><strong>NCF:</strong> ${escHtml(ncf.ncf)} · ${escHtml(ncf.fecha_ncf || '')} · RNC ${escHtml(ncf.rnc || '')}</div>`
    : '';

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Factura ${escHtml(factura.nfactura || '')}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
  .bar { height: 4px; background: #0E1F33; margin: -24px -24px 18px; }
  .empresa { font-size: 18px; font-weight: 700; }
  .muted { color: #5A6B85; font-size: 12px; margin: 2px 0; }
  h1 { font-size: 16px; letter-spacing: 0.08em; text-transform: uppercase; margin: 18px 0 8px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #0E1F33; color: #fff; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; padding: 8px; text-align: left; }
  th.r, td.r { text-align: right; }
  .tot { font-weight: 700; background: #EEF2F8; }
  .meta { margin: 12px 0 18px; }
  @media print { body { margin: 12px; } }
</style></head><body>
  <div class="bar"></div>
  <div class="empresa">${escHtml(nombreEmpresa || 'Empresa')}</div>
  ${direccionEmpresa ? `<div class="muted">${escHtml(direccionEmpresa)}</div>` : ''}
  ${rncEmpresa ? `<div class="muted">RNC: ${escHtml(rncEmpresa)}</div>` : ''}
  <h1>Cuentas por pagar — Factura ${escHtml(factura.nfactura || '—')}</h1>
  <div class="meta">
    <div><strong>Suplidor:</strong> ${escHtml(factura.nombre_suplidor || factura.suplidor || '—')}</div>
    <div><strong>Fecha:</strong> ${escHtml(formatearFecha(factura.fecha))}</div>
    <div><strong>Tipo:</strong> ${escHtml(factura.tipodefactura || '—')} · ${escHtml(factura.documento || '')} ${escHtml(factura.numero != null ? `Nº ${factura.numero}` : '')}</div>
    ${factura.descripcion ? `<div><strong>Descripción:</strong> ${escHtml(factura.descripcion)}</div>` : ''}
    ${ncfHtml}
    <div><strong>Valor:</strong> ${moneyHtml(factura.valor)} · <strong>Pagado:</strong> ${moneyHtml(factura.vplicado)} · <strong>Avance:</strong> ${moneyHtml(factura.v_avance_distribucion)} · <strong>N/C:</strong> ${moneyHtml(factura.v_notas_credito_suplidor)} · <strong>Saldo:</strong> ${moneyHtml(factura.saldo_pendiente)}</div>
  </div>
  <table>
    <thead><tr>
      <th>Cuenta</th><th>Nombre</th><th>Centro</th><th class="r">Valor</th>
    </tr></thead>
    <tbody>${filasLineas}
      <tr class="tot">
        <td colspan="3" style="padding:10px 8px;">Total distribución</td>
        <td class="r" style="padding:10px 8px;">${moneyHtml((factura.lineas || []).reduce((s, l) => s + (parseFloat(l.valor) || 0), 0))}</td>
      </tr>
    </tbody>
  </table>
</body></html>`;
};

/**
 * Vista de detalle de una factura de cuentas por pagar (factcxp).
 * Reutilizable desde mantenimiento, cobros u otras pantallas: basta con
 * `suplidorId` + `nfactura` (o un objeto `factura` ya cargado).
 */
const VistaFacturaCuentasXPagar = ({
  open,
  onClose,
  suplidorId = '',
  nfactura = '',
  factura: facturaProp = null,
}) => {
  const { controller, selectedCompany, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const [factura, setFactura] = useState(facturaProp || {});
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');
  const printedRef = useRef(false);

  const nombreEmpresa =
    selectedCompany?.name ||
    selectedCompany?.empresa ||
    (typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : 'Empresa');
  const direccionEmpresa =
    typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim() ? direccionEmpresaCtx.trim() : '';
  const rncEmpresa = typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';

  const cargarDetalle = useCallback(async () => {
    const sid = String(suplidorId || '').trim();
    const nf = String(nfactura || '').trim();
    if (!sid || !nf || !selectedCompany?.db_empresa) {
      return;
    }
    setCargando(true);
    setErrorCarga('');
    try {
      const res = await fetch(`${servidor}/contabilidadbackend_obtenercuentasxpagar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          suplidor_id: sid,
          nfactura: nf,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'No se pudo cargar el detalle de la factura');
      }
      setFactura(data.factura || {});
    } catch (e) {
      setErrorCarga(e.message || 'Error al cargar el detalle');
      showError(e.message || 'Error al cargar el detalle de la factura');
    } finally {
      setCargando(false);
    }
  }, [suplidorId, nfactura, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  useEffect(() => {
    if (!open) {
      setErrorCarga('');
      printedRef.current = false;
      return undefined;
    }
    if (facturaProp && (facturaProp.nfactura || facturaProp.suplidor)) {
      setFactura(facturaProp);
    }
    cargarDetalle();
    return undefined;
  }, [open, suplidorId, nfactura, cargarDetalle]);

  const htmlFactura = useMemo(
    () =>
      generarHtmlFactura({
        nombreEmpresa,
        direccionEmpresa,
        rncEmpresa,
        factura,
      }),
    [nombreEmpresa, direccionEmpresa, rncEmpresa, factura]
  );

  const handleImprimir = useCallback(() => {
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    ventana.document.open();
    ventana.document.write(htmlFactura);
    ventana.document.close();
    setTimeout(() => {
      ventana.focus();
      ventana.print();
    }, 250);
  }, [htmlFactura, showError]);

  const pagada = String(factura.status || '').trim().toUpperCase() === 'C';
  const ncf = factura.ncf && (factura.ncf.ncf || factura.ncf.rnc) ? factura.ncf : null;
  const lineas = factura.lineas || [];
  const totalLineas = lineas.reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
  const pagos = factura.pagos || [];
  const avances = factura.avances || [];
  const notasCredito = factura.notas_credito || [];
  const asiento = factura.asiento || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          overflow: 'hidden',
          width: { xs: '96vw', md: '90vw' },
          maxWidth: 1280,
          height: { xs: '88vh', md: '86vh' },
          maxHeight: { xs: '88vh', md: '86vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.2, md: 2.6 },
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.accentBlueSoft,
            color: FP.color.accentBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem', flex: 1 }}>
          Factura {factura.nfactura || nfactura || ''}
          {factura.documento ? ` · ${factura.documento}` : ''}
          {factura.numero != null && factura.numero !== '' ? ` Nº ${factura.numero}` : ''}
        </Typography>
        <Chip
          size="small"
          label={etiquetaStatus(factura.status, factura.tipodefactura)}
          sx={{
            fontWeight: 700,
            bgcolor: pagada ? FP.color.successSoft : FP.color.accentBlueSoft,
            color: pagada ? FP.color.successDark : FP.color.accentBlue,
          }}
        />
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2.5, md: 4 },
          pb: { xs: 2.5, md: 3 },
          bgcolor: FP.color.surface,
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 3, md: 4 },
          },
        }}
      >
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: FP.color.navy }} />
          </Box>
        ) : (
          <>
            {errorCarga ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorCarga}
              </Alert>
            ) : null}

            <Paper elevation={0} sx={{ ...cardShellSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
              <Grid container spacing={1.8}>
                <Grid item xs={12} sm={6} md={3}>
                  <Dato label="Suplidor" value={factura.nombre_suplidor || factura.suplidor} strong />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Dato label="Nº factura" value={factura.nfactura} strong />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Dato label="Fecha" value={formatearFecha(factura.fecha)} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Dato label="Tipo" value={factura.tipodefactura} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Dato label="Tasa" value={fmtMoney(factura.tasa)} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Dato label="Forma de pago" value={factura.banco} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Dato label="Referencia" value={factura.referencia} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Dato label="Exento ITBIS" value={factura.excentoitbis} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Dato label="Registrado por" value={factura.creado} />
                </Grid>
                {factura.descripcion ? (
                  <Grid item xs={12}>
                    <Dato label="Descripción" value={factura.descripcion} />
                  </Grid>
                ) : null}
              </Grid>
            </Paper>

            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              {[
                { label: 'Valor', value: factura.valor },
                { label: 'Pagado', value: factura.vplicado },
                { label: 'Avance', value: factura.v_avance_distribucion },
                { label: 'N/C suplidor', value: factura.v_notas_credito_suplidor },
                { label: 'Saldo', value: factura.saldo_pendiente, strong: true },
              ].map((m) => (
                <Grid item xs={6} sm={4} md key={m.label}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...cardShellSx,
                      p: 1.6,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: FP.color.textMuted,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {m.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        color: m.strong ? FP.color.navy : FP.color.textPrimary,
                      }}
                    >
                      ${fmtMoney(m.value)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {ncf ? (
              <Paper elevation={0} sx={{ ...cardShellSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem', mb: 1.5 }}>
                  Comprobante fiscal (NCF)
                </Typography>
                <Grid container spacing={1.6}>
                  <Grid item xs={12} sm={4} md={3}>
                    <Dato label="NCF" value={ncf.ncf} strong />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Dato label="RNC" value={ncf.rnc} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Dato label="Fecha NCF" value={formatearFecha(ncf.fecha_ncf)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Dato label="Tipo servicio" value={ncf.tipo_servicio} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Dato label="Tipo bien" value={ncf.tipo_bien} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <Dato label="Forma pago fiscal" value={ncf.forma_pago_fiscal} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Dato label="Retención" value={`$${fmtMoney(ncf.monto_retencion)}`} />
                  </Grid>
                </Grid>
              </Paper>
            ) : null}

            <SeccionTabla
              titulo="Distribución contable"
              empty="Sin líneas de distribución"
              columnas={[
                { id: 'cuenta', label: 'Cuenta', strong: true },
                {
                  id: 'nombre',
                  label: 'Nombre',
                  title: (r) => r.nombre || '',
                },
                { id: 'localidad', label: 'Centro' },
                {
                  id: 'valor',
                  label: 'Valor',
                  align: 'right',
                  render: (r) => `$${fmtMoney(r.valor)}`,
                },
              ]}
              filas={lineas}
              footer={
                lineas.length > 0 ? (
                  <TableRow
                    sx={{
                      '& td': {
                        borderTop: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                        fontWeight: 800,
                      },
                    }}
                  >
                    <TableCell colSpan={3}>Total</TableCell>
                    <TableCell align="right">${fmtMoney(totalLineas)}</TableCell>
                  </TableRow>
                ) : null
              }
            />

            <SeccionTabla
              titulo="Pagos aplicados"
              empty="Sin pagos registrados"
              columnas={[
                { id: 'fecha', label: 'Fecha', render: (r) => formatearFecha(r.fecha) },
                { id: 'documento', label: 'Documento' },
                { id: 'banco', label: 'Forma de pago' },
                { id: 'referencia', label: 'Referencia' },
                {
                  id: 'valorpagado',
                  label: 'Monto',
                  align: 'right',
                  render: (r) => `$${fmtMoney(r.valorpagado)}`,
                },
              ]}
              filas={pagos}
            />

            <SeccionTabla
              titulo="Avances aplicados"
              empty="Sin avances aplicados"
              columnas={[
                { id: 'fecha', label: 'Fecha', render: (r) => formatearFecha(r.fecha) },
                { id: 'id', label: 'Id', render: (r) => r.id },
                { id: 'ncf', label: 'NCF' },
                { id: 'banco', label: 'Banco / forma' },
                {
                  id: 'valor',
                  label: 'Valor',
                  align: 'right',
                  render: (r) => `$${fmtMoney(r.valor)}`,
                },
              ]}
              filas={avances}
            />

            <SeccionTabla
              titulo="Notas de crédito de suplidor"
              empty="Sin notas de crédito aplicadas"
              columnas={[
                { id: 'fecha', label: 'Fecha', render: (r) => formatearFecha(r.fecha) },
                { id: 'ncred', label: 'N/C' },
                { id: 'ncf', label: 'NCF' },
                {
                  id: 'total',
                  label: 'Total',
                  align: 'right',
                  render: (r) => `$${fmtMoney(r.total)}`,
                },
              ]}
              filas={notasCredito}
            />

            {asiento.length > 0 ? (
              <SeccionTabla
                titulo="Asiento contable"
                empty="Sin asiento"
                columnas={[
                  { id: 'cuenta', label: 'Cuenta', strong: true },
                  { id: 'nombre', label: 'Nombre' },
                  { id: 'concepto', label: 'Concepto' },
                  {
                    id: 'debito',
                    label: 'Débito',
                    align: 'right',
                    render: (r) => (Number(r.debito) > 0 ? `$${fmtMoney(r.debito)}` : ''),
                  },
                  {
                    id: 'credito',
                    label: 'Crédito',
                    align: 'right',
                    render: (r) => (Number(r.credito) > 0 ? `$${fmtMoney(r.credito)}` : ''),
                  },
                ]}
                filas={asiento}
              />
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          startIcon={<PrintIcon />}
          onClick={handleImprimir}
          disabled={cargando || !factura.nfactura}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.4,
            borderColor: FP.color.border,
            color: FP.color.textPrimary,
            '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
          }}
        >
          Imprimir
        </Button>
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VistaFacturaCuentasXPagar;
